import React, { useState, useEffect } from "react"
import { useNavigate, Link } from "react-router-dom"
import EmailTemplateRow from "../components/email-templates/EmailTemplateRow"
import { patchData } from "../utils/api"

/**
 * EmailPreviewGuidePage
 *
 * Table-based view of email templates with thumbnail previews.
 * Fetches email types from the API and displays them in a list.
 * Navigation to create/edit pages uses React Router instead of modals.
 */
const EmailPreviewGuidePage = () => {
  const navigate = useNavigate()
  const [emailTypes, setEmailTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showArchived, setShowArchived] = useState(false)
  const [archivedCount, setArchivedCount] = useState(0)

  const baseUrl = window.location.origin
  const previewBaseUrl = `${baseUrl}/rails/mailers`

  useEffect(() => {
    loadEmailTypes()
  }, [showArchived])

  const loadEmailTypes = async () => {
    try {
      setLoading(true)
      if (showArchived) {
        const response = await fetch('/admin/email_types')
        if (!response.ok) throw new Error('Failed to fetch email types')
        const data = await response.json()
        setEmailTypes(data.filter(et => !et.active))
        setArchivedCount(data.filter(et => !et.active).length)
      } else {
        const [activeResponse, allResponse] = await Promise.all([
          fetch('/admin/email_types?active_only=true'),
          fetch('/admin/email_types'),
        ])
        if (!activeResponse.ok || !allResponse.ok) {
          throw new Error('Failed to fetch email types')
        }
        const activeData = await activeResponse.json()
        const allData = await allResponse.json()
        setEmailTypes(activeData)
        setArchivedCount(allData.length - activeData.length)
      }
    } catch (err) {
      console.error('Error loading email types:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleArchive = async (emailType) => {
    try {
      await patchData(`admin/email_types/${emailType.id}`, {
        email_type: { active: !emailType.active }
      })
      loadEmailTypes()
    } catch (err) {
      console.error('Error toggling archive:', err)
    }
  }

  const handleRename = async (emailType, newName) => {
    try {
      await patchData(`admin/email_types/${emailType.id}`, {
        email_type: { name: newName }
      })
      loadEmailTypes()
    } catch (err) {
      console.error('Error renaming email type:', err)
    }
  }

  const handleTogglePause = async (emailType) => {
    try {
      await patchData(`admin/email_types/${emailType.id}`, {
        email_type: { paused: !emailType.paused }
      })
      loadEmailTypes()
    } catch (err) {
      console.error('Error toggling pause:', err)
    }
  }

  const handleEdit = (emailType) => {
    navigate(`/admin/automated_emails/types/${emailType.key}/templates`)
  }

  const handleCreateTemplate = (emailType) => {
    navigate(`/admin/automated_emails/types/${emailType.key}/templates`)
  }

  if (loading) {
    return (
      <div className="email-templates-page">
        <div className="page-container">
          <div className="loading-state">
            <i className="fa fa-spinner fa-spin fa-2x"></i>
            <p>Loading email templates...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="email-templates-page">
        <div className="page-container">
          <div className="error-state">
            <i className="fa fa-exclamation-triangle fa-2x"></i>
            <p>Error loading email templates: {error}</p>
            <button className="btn-retry" onClick={loadEmailTypes}>
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="email-templates-page">
      <div className="page-container">
        {/* Header */}
        <header className="page-header">
          <div className="header-left">
            {showArchived ? (
              <>
                <a
                  href="#"
                  className="back-link"
                  onClick={(e) => { e.preventDefault(); setShowArchived(false) }}
                >
                  <i className="fa fa-arrow-left"></i> Back to Automated Emails
                </a>
                <h1 className="page-title">Archived Emails</h1>
                <p className="page-subtitle">
                  These emails are hidden from the main list
                </p>
              </>
            ) : (
              <>
                <h1 className="page-title">Automated Emails</h1>
                <p className="page-subtitle">
                  Customize the emails your system sends automatically
                </p>
              </>
            )}
          </div>
          {!showArchived && (
            <div className="header-actions">
              <Link
                to="/admin/automated_emails/new"
                className="btn-new-email"
              >
                <i className="fa fa-plus"></i>
                New Automated Email
              </Link>
              <a
                href={previewBaseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-browse"
              >
                <i className="fa fa-external-link-alt"></i>
                Browse All Previews
              </a>
            </div>
          )}
        </header>

        {/* Templates List */}
        <div className="templates-section">
          {emailTypes.length === 0 ? (
            <div className="empty-state">
              <i className={`fa ${showArchived ? 'fa-archive' : 'fa-inbox'} fa-3x`}></i>
              <h3>{showArchived ? 'No archived emails' : 'No email types configured'}</h3>
              <p>
                {showArchived
                  ? 'Archived emails will appear here.'
                  : 'Email types need to be set up before you can create templates.'}
              </p>
            </div>
          ) : (
            <div className="templates-list">
              {emailTypes.map((emailType) => (
                <EmailTemplateRow
                  key={emailType.id}
                  emailType={emailType}
                  onEdit={handleEdit}
                  onCreateTemplate={handleCreateTemplate}
                  onToggleArchive={handleToggleArchive}
                  onRename={handleRename}
                  onTogglePause={handleTogglePause}
                />
              ))}
            </div>
          )}
        </div>

        {/* Stats Footer */}
        <div className="page-footer">
          <div className="stats">
            {showArchived ? (
              <span className="stat">
                <strong>{emailTypes.length}</strong> archived
              </span>
            ) : (
              <>
                {emailTypes.length > 0 && (
                  <>
                    <span className="stat">
                      <strong>{emailTypes.length}</strong> emails
                    </span>
                    <span className="stat">
                      <strong>{emailTypes.filter(t => t.default_template).length}</strong> with templates
                    </span>
                  </>
                )}
                {archivedCount > 0 && (
                  <a
                    href="#"
                    className="stat stat-link"
                    onClick={(e) => { e.preventDefault(); setShowArchived(true) }}
                  >
                    View {archivedCount} archived
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmailPreviewGuidePage
